import { DATA_TYPES, DataTypes } from '@/features/adp/types/data.types';
import { getAdpSessionNode } from '@/features/adp/hooks/useAdpSessionStore';
import { TransformationTypes } from '@/features/adp/types/transformation.types';
import { SELECTED_COLUMNS_WITH_TYPES_KEY } from '@/features/adp/components/sheets/transformation-sheet/transformationSheet.utils';

// ─── Public types & interfaces ────────────────────────────────────────────────

export type OutputPreviewDataType = (typeof DATA_TYPES)[number];

export type CreatedFileFieldRow = {
  /** Present on new-source rows from `/source-files`; absent on registered-source rows. */
  source_file_field_id?: number;
  field_name: string;
  field_data_type: string;
  /** Present on new-source rows; registered-source rows have no size info. */
  field_size?: number;
  is_business_primary_key?: boolean;
};

export type OutputPreviewColumnDraft = {
  sourceFileFieldId: number;
  fieldName: string;
  selected: boolean;
  fieldDataType: OutputPreviewDataType;
  fieldLength: number;
  isPrimaryKey: boolean;
};

export interface DcOverride {
  columnName: string;
  targetDataType: OutputPreviewDataType;
}

export interface RenameOverride {
  oldColumn: string;
  newColumn: string;
}

export interface DerivedColumn {
  columnName: string;
  dataType: OutputPreviewDataType;
}

interface ReplayColumn {
  columnName: string;
  dataType: OutputPreviewDataType;
}

export type SourceDataGetter = (id: string) => { data: Record<string, unknown> } | null;

// ─── Internal types ───────────────────────────────────────────────────────────

type TransformationEntry = { data: { abbreviation?: string; data?: unknown[] } };

// ─── Constants ────────────────────────────────────────────────────────────────

const ALLOWED_SET = new Set<string>(DATA_TYPES);

// ─── Shared internal helpers ──────────────────────────────────────────────────

/** Returns the transformation entry for a node if its abbreviation matches, otherwise null. */
function getTransformationItems(
  nodeId: string,
  abbreviation: TransformationTypes
): unknown[] | null {
  const record = getAdpSessionNode(nodeId);
  if (!record) return null;
  const entry = record.transformationData as TransformationEntry | undefined;
  if (entry?.data?.abbreviation !== abbreviation) return null;
  const items = entry.data.data;
  return Array.isArray(items) ? items : null;
}

/**
 * Returns the integration entry for a node if its abbreviation matches.
 * Integration nodes (Join `JR`, Union `UN`) persist under `integrationData`,
 * mirror of `getTransformationItems` for the transformation domain.
 */
function getIntegrationItems(
  nodeId: string,
  abbreviation: TransformationTypes
): unknown[] | null {
  const record = getAdpSessionNode(nodeId) as
    | { integrationData?: TransformationEntry }
    | null;
  if (!record) return null;
  const entry = record.integrationData;
  if (entry?.data?.abbreviation !== abbreviation) return null;
  const items = entry.data.data;
  return Array.isArray(items) ? items : null;
}

/** Returns the transformation entry for a node if its abbreviation is one of the given set. */
function getTransformationItemsForAny(
  nodeId: string,
  abbreviations: TransformationTypes[]
): { abbreviation: TransformationTypes; items: unknown[] } | null {
  const record = getAdpSessionNode(nodeId);
  if (!record) return null;
  const entry = record.transformationData as TransformationEntry | undefined;
  const abbreviation = entry?.data?.abbreviation;
  if (!abbreviation || !abbreviations.includes(abbreviation as TransformationTypes)) return null;
  const items = entry.data.data;
  return Array.isArray(items) ? { abbreviation: abbreviation as TransformationTypes, items } : null;
}

/** Extracts the `formData` object from a raw item, or returns null. */
function getFormData(item: unknown): Record<string, unknown> | null {
  if (!item || typeof item !== 'object') return null;
  const { formData } = item as { formData?: Record<string, unknown> };
  return formData ?? null;
}

/** Reads a string field from formData, returning null if absent or wrong type. */
function stringField(formData: Record<string, unknown>, key: string): string | null {
  const v = formData[key];
  return typeof v === 'string' ? v : null;
}

/**
 * Parses a comma-separated column string (supports both `NewColumns` and
 * `newColumns` casing) from a formData entry, returning trimmed non-empty names.
 */
function parseSplitColumnNames(formData: Record<string, unknown>): string[] {
  const raw = stringField(formData, 'NewColumns') ?? stringField(formData, 'newColumns') ?? '';
  if (!raw.trim()) return [];
  return raw
    .split(',')
    .map(col => col.trim())
    .filter(Boolean);
}

/** Extracts derived columns for a single transformation formData entry. */
function extractDerivedColumnsFromFormData(
  abbreviation: TransformationTypes,
  formData: Record<string, unknown>
): DerivedColumn[] {
  const parsers: Partial<
    Record<TransformationTypes, (data: Record<string, unknown>) => DerivedColumn[]>
  > = {
    [TransformationTypes.Split]: data =>
      parseSplitColumnNames(data).map(columnName => ({
        columnName,
        dataType: DataTypes.string,
      })),
    [TransformationTypes.Zscore]: data => {
      const columnName = (stringField(data, 'new_column') ?? '').trim();
      return columnName ? [{ columnName, dataType: DataTypes.float }] : [];
    },
    [TransformationTypes.BoxCox]: data => {
      const columnName = (stringField(data, 'new_column_name') ?? '').trim();
      return columnName ? [{ columnName, dataType: DataTypes.float }] : [];
    },
    [TransformationTypes.DateSplit]: data => {
      const baseColumnName = (stringField(data, 'DateColumn') ?? '').trim();
      if (!baseColumnName) return [];
      return [
        { columnName: `${baseColumnName}_year`, dataType: DataTypes.int },
        { columnName: `${baseColumnName}_month`, dataType: DataTypes.int },
        { columnName: `${baseColumnName}_day`, dataType: DataTypes.int },
      ];
    },
    [TransformationTypes.Concat]: data => {
      const columnName = (stringField(data, 'New_ColumnName') ?? '').trim();
      return columnName ? [{ columnName, dataType: DataTypes.string }] : [];
    },
    [TransformationTypes.DateToInteger]: data => {
      const columnName = (stringField(data, 'NewDateKeyColumnName') ?? '').trim();
      return columnName ? [{ columnName, dataType: DataTypes.int }] : [];
    },
    [TransformationTypes.Cumulative]: data => {
      const columnName = (stringField(data, 'NewColumnName') ?? '').trim();
      return columnName ? [{ columnName, dataType: DataTypes.int }] : [];
    },
    [TransformationTypes.Lookup]: data => {
      // SelectedColumnsWithTypes is { name: string; dataType: string }[] stored
      // by TransformationSheet at save time, containing type info from the API.
      const withTypes = data[SELECTED_COLUMNS_WITH_TYPES_KEY];
      if (!Array.isArray(withTypes)) return [];
      return withTypes
        .filter(
          (entry): entry is { name: string; dataType: string } =>
            entry != null &&
            typeof entry === 'object' &&
            typeof (entry as { name?: unknown }).name === 'string' &&
            typeof (entry as { dataType?: unknown }).dataType === 'string'
        )
        .map(entry => ({
          columnName: entry.name.trim(),
          dataType: normalizeOutputPreviewFieldDataType(entry.dataType),
        }))
        .filter(col => col.columnName.length > 0);
    },
    [TransformationTypes.Transpose]: data => {
      const headers = data.transposeHeaders;
      if (!Array.isArray(headers)) return [];
      return headers
        .filter(
          (entry): entry is Record<string, unknown> => entry != null && typeof entry === 'object'
        )
        .map(entry => {
          const name = typeof entry.name === 'string' ? entry.name.trim() : '';
          const rawType = typeof entry.dataType === 'string' ? entry.dataType : '';
          return {
            columnName: name,
            dataType: normalizeOutputPreviewFieldDataType(rawType),
          };
        })
        .filter(col => col.columnName.length > 0);
    },
  };

  return parsers[abbreviation]?.(formData) ?? [];
}

// ─── Public utilities ─────────────────────────────────────────────────────────

export function labelForOutputPreviewDataType(dt: string): string {
  const s = dt.trim();
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Trim + lowercase only; if not one of the allowed API types, default to `string` for form/select stability. */
export function normalizeOutputPreviewFieldDataType(raw: string): OutputPreviewDataType {
  const n = raw.trim().toLowerCase();
  if (ALLOWED_SET.has(n)) return n as OutputPreviewDataType;
  return DataTypes.string;
}

export function isCreatedFileFieldRow(x: unknown): x is CreatedFileFieldRow {
  if (x === null || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  // Only `field_name` + `field_data_type` are guaranteed across both paths
  // (new-source `/source-files` response and registered-source listing).
  // `source_file_field_id` and `field_size` are optional — registered-source
  // rows have neither, but downstream code handles their absence.
  return typeof o.field_name === 'string' && typeof o.field_data_type === 'string';
}

export function buildColumnsFromCreatedFiles(
  files: unknown[],
  dcOverrides: DcOverride[] = [],
  renameOverrides: RenameOverride[] = [],
  derivedColumns: DerivedColumn[] = [],
  dropFields: string[] = []
): OutputPreviewColumnDraft[] {
  // Build a fast lookup: lowercase column name → target data type
  const overrideMap = new Map<string, OutputPreviewDataType>();
  for (const o of dcOverrides) {
    overrideMap.set(o.columnName.toLowerCase(), o.targetDataType);
  }
  // Build a fast lookup: lowercase old column name -> new column name
  const renameMap = new Map<string, string>();
  for (const o of renameOverrides) {
    renameMap.set(o.oldColumn.toLowerCase(), o.newColumn);
  }
  // Build a fast lookup: lowercase dropped column names — these are excluded entirely
  const dropSet = new Set(dropFields.map(c => c.toLowerCase()));

  const rows = files
    .filter(isCreatedFileFieldRow)
    .filter(f => !dropSet.has(f.field_name.toLowerCase()));
  const mappedRows = rows.map(f => {
    const renamedFieldName = renameMap.get(f.field_name.toLowerCase()) ?? f.field_name;
    const sourceDataType = normalizeOutputPreviewFieldDataType(f.field_data_type);
    const overriddenType =
      overrideMap.get(renamedFieldName.toLowerCase()) ??
      overrideMap.get(f.field_name.toLowerCase());
    const fieldDataType = overriddenType ?? sourceDataType;
    const isString = fieldDataType === 'string';
    // Registered-source rows have no `field_size` (only new-source `/source-files`
    // responses do). Missing size → default 1024 like a freshly-converted string,
    // not `1` — otherwise every registered-source string column lands in postgres
    // as `varchar(1)`.
    const fieldSizeProvided = typeof f.field_size === 'number';
    const rawSize = fieldSizeProvided ? f.field_size : 0;
    const normalizedSize = rawSize > 0 ? rawSize : 1;
    const convertedToString = fieldDataType === 'string' && sourceDataType !== 'string';
    let fieldLength = 0;
    if (isString) {
      fieldLength = convertedToString || !fieldSizeProvided
        ? 1024
        : Math.min(1024, Math.max(1, normalizedSize));
    }

    return {
      sourceFileFieldId: typeof f.source_file_field_id === 'number' ? f.source_file_field_id : 0,
      fieldName: renamedFieldName,
      selected: true,
      fieldDataType,
      fieldLength,
      isPrimaryKey: Boolean(f.is_business_primary_key),
    };
  });

  const existingNames = new Set(mappedRows.map(row => row.fieldName.trim().toLowerCase()));

  const derivedRows: OutputPreviewColumnDraft[] = [];
  for (const derivedColumn of derivedColumns) {
    const fieldName = derivedColumn.columnName.trim();
    if (!fieldName) continue;
    const key = fieldName.toLowerCase();
    if (existingNames.has(key)) continue;
    existingNames.add(key);

    const isString = derivedColumn.dataType === DataTypes.string;
    derivedRows.push({
      // Transformation-created columns are derived fields.
      sourceFileFieldId: 0,
      fieldName,
      selected: true,
      fieldDataType: derivedColumn.dataType,
      fieldLength: isString ? 1024 : 0,
      isPrimaryKey: false,
    });
  }

  return [...mappedRows, ...derivedRows];
}

// ─── Transformation override extractors ──────────────────────────────────────

/**
 * Given a list of upstream transformation node IDs, reads each one from the
 * session store and collects every Data Type Conversion (abbreviation === "DC")
 * override as a flat `{ columnName, targetDataType }` list.
 *
 * Called with a store snapshot so it never triggers re-renders.
 */
export function extractDcOverrides(upstreamTransformationNodeIds: string[]): DcOverride[] {
  const overrides: DcOverride[] = [];

  for (const nodeId of upstreamTransformationNodeIds) {
    const items = getTransformationItems(nodeId, TransformationTypes.DataTypeConversion);
    if (!items) continue;

    for (const item of items) {
      const formData = getFormData(item);
      if (!formData) continue;
      const columnName = stringField(formData, 'columnName');
      const rawType = stringField(formData, 'targetDataType');
      if (!columnName || !rawType) continue;
      overrides.push({ columnName, targetDataType: normalizeOutputPreviewFieldDataType(rawType) });
    }
  }

  return overrides;
}

/**
 * Given a list of upstream transformation node IDs, reads each one from the
 * session store and collects every Rename Column (abbreviation === "RC")
 * override as a flat `{ oldColumn, newColumn }` list.
 */
export function extractRenameOverrides(upstreamTransformationNodeIds: string[]): RenameOverride[] {
  const overrides: RenameOverride[] = [];

  for (const nodeId of upstreamTransformationNodeIds) {
    const items = getTransformationItems(nodeId, TransformationTypes.RenameColumn);
    if (!items) continue;

    for (const item of items) {
      const formData = getFormData(item);
      if (!formData) continue;
      const oldColumn = stringField(formData, 'oldColumn');
      const newColumn = stringField(formData, 'newColumn');
      if (!oldColumn || !newColumn) continue;
      overrides.push({ oldColumn, newColumn });
    }
  }

  return overrides;
}

/**
 * Given a list of upstream transformation node IDs, reads each one from the
 * session store and collects every Split (abbreviation === "SP") `NewColumns`
 * value as a flattened list of column names.
 */
export function extractSplitColumns(upstreamTransformationNodeIds: string[]): string[] {
  const columns: string[] = [];

  for (const nodeId of upstreamTransformationNodeIds) {
    const items = getTransformationItems(nodeId, TransformationTypes.Split);
    if (!items) continue;

    for (const item of items) {
      const formData = getFormData(item);
      if (!formData) continue;
      // parseSplitColumnNames handles both casing variants and the comma-split
      columns.push(...parseSplitColumnNames(formData));
    }
  }

  return columns;
}

/**
 * Given upstream transformation node IDs, extracts target-only derived columns
 * created by transformation types that introduce new fields.
 *
 * Current support:
 * - SP: `NewColumns`/`newColumns` => string
 * - ZS: `new_column` => float
 * - BC: `new_column_name` => float
 * - DS: `DateColumn` => `${DateColumn}_year`, `${DateColumn}_month`, `${DateColumn}_day` => int
 * - DI: `NewDateKeyColumnName` => int
 * - CC: `New_ColumnName` => string
 * - CU: `NewColumnName` => int
 * - LK: `SelectedColumnsWithTypes[].{ name, dataType }`
 * - TP: `transposeHeaders[].{ name, dataType }`; source columns dropped from the
 *   preview (Transpose `columnConfig` with `ignore` / `transpose`) use `extractTransposeIgnoreColumns`
 */
/**
 * Walks upstream **integration** nodes whose abbreviation is `JR` (Join) and
 * emits the join-secondary table's `joinTableColumns` as derived columns. Data
 * types are resolved by looking up the secondary source (matched by
 * `joinTableDataSource` === `source_name`) in `upstreamIds` and reading its
 * `source_files`; falls back to `string` when the field isn't present.
 *
 * Without this, OutputPreview only emits the primary source's columns and the
 * downstream Column Mapping destination dropdown loses every join-table field.
 */
export function extractJoinDerivedColumns(
  upstreamIntegrationNodeIds: string[],
  upstreamIds: string[],
  getSourceDataByNodeId: SourceDataGetter
): DerivedColumn[] {
  if (upstreamIntegrationNodeIds.length === 0) return [];

  // Build a lookup: source_name → source_files (for data-type resolution).
  const filesBySourceName = new Map<string, unknown[]>();
  for (const id of upstreamIds) {
    const data = getSourceDataByNodeId(id)?.data;
    if (!data) continue;
    const name = typeof data.source_name === 'string' ? data.source_name : null;
    const files = Array.isArray(data.source_files) ? data.source_files : null;
    if (name && files) filesBySourceName.set(name, files);
  }

  const lookupDataType = (sourceName: string, fieldName: string): OutputPreviewDataType => {
    const files = filesBySourceName.get(sourceName);
    if (!files) return DataTypes.string;
    for (const f of files) {
      if (!f || typeof f !== 'object') continue;
      const o = f as Record<string, unknown>;
      if (typeof o.field_name === 'string' && o.field_name === fieldName) {
        const raw = typeof o.field_data_type === 'string' ? o.field_data_type : '';
        return normalizeOutputPreviewFieldDataType(raw);
      }
    }
    return DataTypes.string;
  };

  const out: DerivedColumn[] = [];
  for (const nodeId of upstreamIntegrationNodeIds) {
    const items = getIntegrationItems(nodeId, TransformationTypes.Join);
    if (!items) continue;
    for (const item of items) {
      const formData = getFormData(item);
      if (!formData) continue;
      const joinTableDataSource = stringField(formData, 'joinTableDataSource') ?? '';
      const rawColumns = stringField(formData, 'joinTableColumns') ?? '';
      if (!rawColumns.trim()) continue;
      const names = rawColumns
        .split(',')
        .map(n => n.trim())
        .filter(n => n.length > 0);
      // Parallel data-type CSV stored alongside `joinTableColumns` since Join
      // PR follow-up. When present, prefer it over the source-files lookup —
      // it survives canvas edits that would otherwise leave the lookup empty.
      const rawTypes = stringField(formData, 'joinTableColumnTypes') ?? '';
      const types = rawTypes ? rawTypes.split(',').map(t => t.trim()) : [];
      names.forEach((name, idx) => {
        const rawType = types[idx];
        const dataType = rawType
          ? normalizeOutputPreviewFieldDataType(rawType)
          : lookupDataType(joinTableDataSource, name);
        out.push({ columnName: name, dataType });
      });
    }
  }
  return out;
}

export function extractDerivedColumns(upstreamTransformationNodeIds: string[]): DerivedColumn[] {
  const derivedColumns: DerivedColumn[] = [];

  for (const nodeId of upstreamTransformationNodeIds) {
    const result = getTransformationItemsForAny(nodeId, [
      TransformationTypes.Split,
      TransformationTypes.Zscore,
      TransformationTypes.BoxCox,
      TransformationTypes.DateSplit,
      TransformationTypes.DateToInteger,
      TransformationTypes.Concat,
      TransformationTypes.Cumulative,
      TransformationTypes.Lookup,
      TransformationTypes.Transpose,
    ]);
    if (!result) continue;

    for (const item of result.items) {
      const formData = getFormData(item);
      if (!formData) continue;
      derivedColumns.push(...extractDerivedColumnsFromFormData(result.abbreviation, formData));
    }
  }

  return derivedColumns;
}

// ─── Payload helpers ──────────────────────────────────────────────────────────

/**
 * Extracts the `dropFieldsList` string array from a single raw transformation
 * item's `formData`. Returns an empty array if the field is absent or invalid.
 */
function extractDropFieldsFromItem(item: unknown): string[] {
  const formData = getFormData(item);
  if (!formData) return [];
  const list = formData.dropFieldsList;
  if (!Array.isArray(list)) return [];
  return list
    .filter((col): col is string => typeof col === 'string' && col.trim().length > 0)
    .map(col => col.trim());
}

/**
 * Given a list of upstream transformation node IDs, reads each one from the
 * session store and collects every Drop Fields (abbreviation === "DF")
 * `dropFieldsList` entry as a flat list of column names to exclude.
 *
 * These columns must be removed from both the output preview table and the
 * column mapping source rows. Transpose `columnConfig` rows whose `condition`
 * is `"ignore"` or `"transpose"` are handled separately via
 * `extractTransposeIgnoreColumns` (merged by callers alongside this list).
 */
export function extractDropFieldsColumns(upstreamTransformationNodeIds: string[]): string[] {
  return upstreamTransformationNodeIds
    .flatMap(nodeId => getTransformationItems(nodeId, TransformationTypes.DropFields) ?? [])
    .flatMap(extractDropFieldsFromItem);
}

/**
 * Source column names from a single Transpose (`TP`) `formData` that should not
 * appear as ordinary source fields in preview / mapping: `columnConfig` rows with
 * `condition` `"ignore"` (dropped) or `"transpose"` (pivoted; replaced by
 * `transposeHeaders` in the target shape).
 */
function extractTransposeIgnoreFromFormData(formData: Record<string, unknown>): string[] {
  const raw = formData.columnConfig;
  if (!Array.isArray(raw)) return [];
  const names: string[] = [];
  for (const row of raw) {
    if (row == null || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const columnName = typeof r.columnName === 'string' ? r.columnName.trim() : '';
    if (!columnName) continue;
    const condition = typeof r.condition === 'string' ? r.condition.trim().toLowerCase() : '';
    if (condition === 'ignore' || condition === 'transpose') names.push(columnName);
  }
  return names;
}

/**
 * Flattens {@link extractTransposeIgnoreFromFormData} across all Transpose items
 * on the given upstream transformation node IDs. Callers merge the result with
 * explicit Drop Fields when building column lists.
 */
export function extractTransposeIgnoreColumns(upstreamTransformationNodeIds: string[]): string[] {
  const columns: string[] = [];
  for (const nodeId of upstreamTransformationNodeIds) {
    const items = getTransformationItems(nodeId, TransformationTypes.Transpose);
    if (!items) continue;
    for (const item of items) {
      const formData = getFormData(item);
      if (!formData) continue;
      columns.push(...extractTransposeIgnoreFromFormData(formData));
    }
  }
  return columns;
}

function applyDropFieldsReplay(
  columns: ReplayColumn[],
  formData: Record<string, unknown>
): ReplayColumn[] {
  const dropped = Array.isArray(formData.dropFieldsList)
    ? (formData.dropFieldsList as unknown[])
        .filter((c): c is string => typeof c === 'string' && c.trim().length > 0)
        .map(c => c.trim().toLowerCase())
    : [];
  if (dropped.length === 0) return columns;
  const dropSet = new Set(dropped);
  return columns.filter(c => !dropSet.has(c.columnName.toLowerCase()));
}

function applyAggregationReplay(
  columns: ReplayColumn[],
  formData: Record<string, unknown>
): ReplayColumn[] {
  const groupByColumns = Array.isArray(formData.groupByColumns)
    ? (formData.groupByColumns as unknown[])
        .filter((c): c is string => typeof c === 'string' && c.trim().length > 0)
        .map(c => c.trim())
    : [];
  const groupBySet = new Set(groupByColumns.map(c => c.toLowerCase()));
  const kept = columns.filter(c => groupBySet.has(c.columnName.toLowerCase()));

  const rawAggList = Array.isArray(formData.aggregateList) ? formData.aggregateList : [];
  const newColumns: ReplayColumn[] = [];
  for (const row of rawAggList as unknown[]) {
    const r = row != null && typeof row === 'object' ? (row as Record<string, unknown>) : {};
    const columnName = (typeof r.newColumnName === 'string' ? r.newColumnName : '').trim();
    const rawType = (typeof r.newDataType === 'string' ? r.newDataType : '').trim();
    if (!columnName) continue;
    newColumns.push({
      columnName,
      dataType: rawType ? normalizeOutputPreviewFieldDataType(rawType) : DataTypes.float,
    });
  }

  return [...kept, ...newColumns];
}

function replayColumnsForNode(
  columns: ReplayColumn[],
  nodeId: string
): { columns: ReplayColumn[]; hasAggregation: boolean } {
  const result = getTransformationItemsForAny(nodeId, [
    TransformationTypes.DropFields,
    TransformationTypes.Aggregation,
  ]);
  if (!result) return { columns, hasAggregation: false };

  let next = columns;
  let hasAggregation = false;
  for (const item of result.items) {
    const formData = getFormData(item);
    if (!formData) continue;
    if (result.abbreviation === TransformationTypes.DropFields) {
      next = applyDropFieldsReplay(next, formData);
      continue;
    }
    if (result.abbreviation === TransformationTypes.Aggregation) {
      hasAggregation = true;
      next = applyAggregationReplay(next, formData);
    }
  }

  return { columns: next, hasAggregation };
}

function extractDerivedFromReplay(
  replay: ReplayColumn[],
  sourceNameSet: Set<string>
): DerivedColumn[] {
  const uniqueDerived = new Map<string, DerivedColumn>();
  for (const col of replay) {
    const key = col.columnName.toLowerCase();
    if (sourceNameSet.has(key) || uniqueDerived.has(key)) continue;
    uniqueDerived.set(key, {
      columnName: col.columnName,
      dataType: col.dataType,
    });
  }
  return [...uniqueDerived.values()];
}

export function extractAggregationReplayProjection(
  upstreamTransformationNodeIds: string[],
  files: unknown[]
): { dropFields: string[]; derivedColumns: DerivedColumn[] } | null {
  const baseColumns: ReplayColumn[] = files
    .filter(isCreatedFileFieldRow)
    .map(f => ({
      columnName: f.field_name.trim(),
      dataType: normalizeOutputPreviewFieldDataType(f.field_data_type),
    }))
    .filter(c => c.columnName.length > 0);

  let replay = baseColumns;
  let hasAggregation = false;

  for (const nodeId of upstreamTransformationNodeIds) {
    const replayResult = replayColumnsForNode(replay, nodeId);
    replay = replayResult.columns;
    hasAggregation = hasAggregation || replayResult.hasAggregation;
  }

  if (!hasAggregation) return null;

  const sourceNameSet = new Set(
    files
      .filter(isCreatedFileFieldRow)
      .map(f => f.field_name.trim().toLowerCase())
      .filter(Boolean)
  );
  const replayNameSet = new Set(replay.map(c => c.columnName.toLowerCase()));

  const dropFields = files
    .filter(isCreatedFileFieldRow)
    .map(f => f.field_name.trim())
    .filter(name => Boolean(name) && !replayNameSet.has(name.toLowerCase()));

  return {
    dropFields,
    derivedColumns: extractDerivedFromReplay(replay, sourceNameSet),
  };
}

export function resolveCreatedFilesPayload(
  upstreamNodeIds: string[],
  getSourceDataByNodeId: SourceDataGetter
): unknown[] {
  for (const id of upstreamNodeIds) {
    const files = getSourceDataByNodeId(id)?.data?.source_files;
    if (Array.isArray(files) && files.length > 0) {
      return files;
    }
  }
  return [];
}

export function isOutputPreviewStringType(dt: string): boolean {
  return dt === 'string';
}
